// Cache for document preview signed URLs.
// - Synchronous in-memory Map for fast reads from React render code.
// - Mirrored to IndexedDB (async, fire-and-forget) so reloads don't refetch.
// - Scoped by workspaceId + a stable key (document or version).
// - TTL is kept below the signed URL expiry to avoid returning expired URLs.
// - Automatic invalidation when the URL is past `expiresAt` and when the
//   caller switches workspace via `invalidateWorkspace`.

export type PreviewCacheEntry = {
  workspaceId: string | null;
  key: string; // logical key (documentPreviewKey / versionPreviewKey)
  url: string;
  mime: string | null;
  expiresAt: number; // epoch ms
};

const DEFAULT_TTL_MS = 50 * 60 * 1000; // 50 min (signed URLs expire at 60 min)
const DB_NAME = "scooterp-preview-cache";
const DB_VERSION = 1;
const STORE = "entries";
const LEGACY_LS_PREFIX = "scooterp-preview-cache:";

// ---------- in-flight request registry ----------
// AbortControllers registered here get aborted on signOut / clear so no
// signed-URL fetch leaks past the session boundary.
const activeControllers = new Set<AbortController>();

const registerAbort = (controller: AbortController) => {
  activeControllers.add(controller);
  const cleanup = () => activeControllers.delete(controller);
  controller.signal.addEventListener("abort", cleanup, { once: true });
  return () => {
    controller.signal.removeEventListener("abort", cleanup);
    activeControllers.delete(controller);
  };
};

const abortAll = () => {
  for (const c of activeControllers) {
    try { c.abort(); } catch { /* ignore */ }
  }
  activeControllers.clear();
};

// ---------- legacy localStorage migration ----------
// Older builds persisted preview entries under localStorage keys prefixed with
// `scooterp-preview-cache:`. Copy any surviving entries into IndexedDB the
// first time we hydrate so users don't lose warm cache after upgrading.
let legacyMigrated = false;
const migrateFromLocalStorage = () => {
  if (legacyMigrated) return;
  legacyMigrated = true;
  if (typeof localStorage === "undefined") return;
  try {
    const now = Date.now();
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(LEGACY_LS_PREFIX)) continue;
      toRemove.push(k);
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw) as Partial<PreviewCacheEntry>;
        if (!parsed?.key || !parsed?.url || typeof parsed?.expiresAt !== "number") continue;
        if (parsed.expiresAt <= now) continue;
        const entry: PreviewCacheEntry = {
          workspaceId: parsed.workspaceId ?? null,
          key: parsed.key,
          url: parsed.url,
          mime: parsed.mime ?? null,
          expiresAt: parsed.expiresAt,
        };
        store.set(buildKey(entry.workspaceId, entry.key), entry);
        idbPut(entry);
      } catch { /* skip malformed entry */ }
    }
    for (const k of toRemove) {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
    }
    if (toRemove.length > 0) {
      console.info("[preview-cache] migrated legacy localStorage entries", { count: toRemove.length });
    }
  } catch { /* ignore */ }
};

// ---------- in-memory layer ----------
const store = new Map<string, PreviewCacheEntry>();
const buildKey = (workspaceId: string | null | undefined, key: string) =>
  `${workspaceId ?? "_"}::${key}`;

// ---------- IndexedDB layer ----------
const hasIDB = () =>
  typeof indexedDB !== "undefined" && typeof window !== "undefined";

let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (!hasIDB()) return Promise.reject(new Error("no-idb"));
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "id" });
        os.createIndex("workspaceId", "workspaceId", { unique: false });
        os.createIndex("expiresAt", "expiresAt", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch((e) => {
    dbPromise = null;
    throw e;
  });
  return dbPromise;
};

const tx = async <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | void> => {
  if (!hasIDB()) return;
  try {
    const db = await openDB();
    return await new Promise<T | void>((resolve, reject) => {
      const t = db.transaction(STORE, mode);
      const os = t.objectStore(STORE);
      const req = fn(os);
      t.oncomplete = () => resolve(req && "result" in req ? (req.result as T) : undefined);
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  } catch {
    /* IDB not available / blocked — ignore */
  }
};

const idbPut = (entry: PreviewCacheEntry) => {
  const id = buildKey(entry.workspaceId, entry.key);
  void tx("readwrite", (os) => os.put({ ...entry, id }));
};

const idbDelete = (workspaceId: string | null | undefined, key: string) => {
  const id = buildKey(workspaceId, key);
  void tx("readwrite", (os) => os.delete(id));
};

const idbClear = () => {
  void tx("readwrite", (os) => os.clear());
};

// Delete every entry for the given workspaceId.
const idbDeleteByWorkspace = (workspaceId: string | null) => {
  if (!hasIDB()) return;
  void (async () => {
    try {
      const db = await openDB();
      await new Promise<void>((resolve, reject) => {
        const t = db.transaction(STORE, "readwrite");
        const os = t.objectStore(STORE);
        const idx = os.index("workspaceId");
        // IDB does not index null well — fall back to full sweep when null.
        if (workspaceId === null) {
          const req = os.openCursor();
          req.onsuccess = () => {
            const cur = req.result;
            if (!cur) return;
            const v = cur.value as PreviewCacheEntry;
            if (v.workspaceId === null) cur.delete();
            cur.continue();
          };
        } else {
          const req = idx.openCursor(IDBKeyRange.only(workspaceId));
          req.onsuccess = () => {
            const cur = req.result;
            if (!cur) return;
            cur.delete();
            cur.continue();
          };
        }
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
      });
    } catch {
      /* ignore */
    }
  })();
};

// Load all non-expired entries for a workspace into the in-memory Map.
const hydrateWorkspace = async (workspaceId: string | null): Promise<void> => {
  if (!hasIDB()) return;
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const t = db.transaction(STORE, "readwrite");
      const os = t.objectStore(STORE);
      const req =
        workspaceId === null
          ? os.openCursor()
          : os.index("workspaceId").openCursor(IDBKeyRange.only(workspaceId));
      const now = Date.now();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        const v = cur.value as PreviewCacheEntry & { id: string };
        if (v.expiresAt <= now) {
          cur.delete();
        } else if (workspaceId === null ? v.workspaceId === null : v.workspaceId === workspaceId) {
          store.set(buildKey(v.workspaceId, v.key), {
            workspaceId: v.workspaceId,
            key: v.key,
            url: v.url,
            mime: v.mime,
            expiresAt: v.expiresAt,
          });
        }
        cur.continue();
      };
      t.oncomplete = () => resolve();
      t.onerror = () => reject(t.error);
    });
  } catch {
    /* ignore */
  }
};

// ---------- public API ----------
export const previewCache = {
  get(workspaceId: string | null | undefined, key: string): PreviewCacheEntry | null {
    const k = buildKey(workspaceId, key);
    const entry = store.get(k) ?? null;
    if (!entry) return null;
    if (entry.expiresAt <= Date.now()) {
      store.delete(k);
      idbDelete(workspaceId, key);
      return null;
    }
    return entry;
  },
  set(
    workspaceId: string | null | undefined,
    key: string,
    value: { url: string; mime: string | null; ttlMs?: number },
  ) {
    const entry: PreviewCacheEntry = {
      workspaceId: workspaceId ?? null,
      key,
      url: value.url,
      mime: value.mime,
      expiresAt: Date.now() + (value.ttlMs ?? DEFAULT_TTL_MS),
    };
    store.set(buildKey(workspaceId, key), entry);
    idbPut(entry);
  },
  has(workspaceId: string | null | undefined, key: string): boolean {
    return !!previewCache.get(workspaceId, key);
  },
  invalidate(workspaceId: string | null | undefined, key: string) {
    store.delete(buildKey(workspaceId, key));
    idbDelete(workspaceId, key);
  },
  // Drop everything belonging to a workspace — call this when the active
  // workspace changes so a user never sees another tenant's signed URLs.
  invalidateWorkspace(workspaceId: string | null) {
    for (const [k, v] of store) {
      if (v.workspaceId === workspaceId) store.delete(k);
    }
    idbDeleteByWorkspace(workspaceId);
  },
  // Remove every expired entry from both layers (safe to call periodically).
  sweepExpired() {
    const now = Date.now();
    for (const [k, v] of store) {
      if (v.expiresAt <= now) store.delete(k);
    }
    if (!hasIDB()) return;
    void tx("readwrite", (os) => {
      const idx = os.index("expiresAt");
      const req = idx.openCursor(IDBKeyRange.upperBound(now));
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) return;
        cur.delete();
        cur.continue();
      };
    });
  },
  // Async — call once on mount / workspace change to populate the in-memory
  // layer from IndexedDB so cache hits work after a reload. Also migrates any
  // surviving entries from the legacy localStorage layer the first time.
  async hydrate(workspaceId: string | null) {
    migrateFromLocalStorage();
    await hydrateWorkspace(workspaceId);
  },
  // Register an in-flight AbortController so `clear()` / signOut can cancel it.
  registerAbort,
  // Abort every registered in-flight request without touching cached entries.
  abortAll,
  // Full wipe: aborts pending requests AND removes every cached entry from
  // both layers. Call on signOut so nothing leaks into the next session.
  clear() {
    abortAll();
    store.clear();
    idbClear();
  },
};

export const documentPreviewKey = (docId: string, version: number | string) =>
  `doc:${docId}:v${version}`;
export const versionPreviewKey = (versionId: string) => `ver:${versionId}`;

// Detects best preview mode based on MIME type / filename.
export type PreviewKind = "image" | "pdf" | "video" | "audio" | "text" | "download";

export const detectPreviewKind = (
  mime: string | null | undefined,
  filename?: string | null,
): PreviewKind => {
  const m = (mime ?? "").toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.includes("pdf")) return "pdf";
  if (m.startsWith("video/")) return "video";
  if (m.startsWith("audio/")) return "audio";
  if (m.startsWith("text/") || m.includes("json") || m.includes("xml")) return "text";

  const ext = (filename ?? "").split(".").pop()?.toLowerCase() ?? "";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"].includes(ext)) return "image";
  if (ext === "pdf") return "pdf";
  if (["mp4", "webm", "mov", "m4v"].includes(ext)) return "video";
  if (["mp3", "wav", "ogg", "m4a", "flac"].includes(ext)) return "audio";
  if (["txt", "md", "json", "xml", "csv", "log"].includes(ext)) return "text";
  return "download";
};
